// Card sleeves (card backs), a collectible cosmetic. Each sleeve is drawn procedurally onto a 2D
// canvas, so the whole catalog ships with zero art assets and renders crisp at any size. The SAME
// draw routine paints the WebGL card-back texture (DuelArena) and the customizer swatches
// (CharCreate), so what you pick is exactly what you duel with.
//
// Ownership lives server-side (everyone starts with only DEFAULT_SLEEVE); the selected id rides on
// the avatar payload. Keep designs DARK and tasteful, these sit behind bright card art, so a loud
// back would fight the board.

export interface SleeveDef {
  id: string;
  name: string;
  rarity: "common" | "rare" | "epic" | "legendary";
  /** "image" → render `img` (the real card back); "procedural" → drawn by drawSleeve(). */
  kind: "image" | "procedural";
  /** Same-origin image URL for image sleeves (WebGL-texture-safe). */
  img?: string;
  /** Two representative colors for catalog chips / fallbacks. */
  swatch: [string, string];
}

// The DEFAULT is the real classic Yu-Gi-Oh card back (the brown/orange swirl), served same-origin
// from public/cardback.jpg, everyone owns it. It's the text-free YGOPro `cover.jpg` (no Konami /
// "Yu-Gi-Oh!" wordmark). The procedural neon designs are collectibles unlocked on top. Keep that
// ordering: catalog shows the default first.
export const SLEEVES: SleeveDef[] = [
  // Everyone owns the classic back; the neon designs below are collectibles.
  { id: "default", name: "Classic Back", rarity: "common", kind: "image", img: "/cardback.jpg", swatch: ["#3a1d0c", "#e8a44a"] },
  { id: "df-cyber", name: "VRAINS Cyber", rarity: "rare", kind: "procedural", swatch: ["#13213f", "#4fd4ff"] },
  { id: "crimson", name: "Crimson Sovereign", rarity: "rare", kind: "procedural", swatch: ["#2a0c12", "#ff5d6c"] },
  { id: "abyss", name: "Abyssal Circuit", rarity: "rare", kind: "procedural", swatch: ["#0a0f1f", "#8a6bff"] },
  { id: "verdant", name: "Verdant Code", rarity: "rare", kind: "procedural", swatch: ["#0a1e15", "#54e6a0"] },
  { id: "aureate", name: "Aureate Crest", rarity: "epic", kind: "procedural", swatch: ["#1b1606", "#ffce5a"] },
  { id: "obsidian", name: "Obsidian Null", rarity: "epic", kind: "procedural", swatch: ["#0a0a0d", "#c9d4e6"] },
  { id: "prism", name: "Prismatic Drive", rarity: "legendary", kind: "procedural", swatch: ["#0c1430", "#7af0ff"] },
];

/** Everyone starts sleeveless = the classic card back. */
export const DEFAULT_SLEEVE = "default";
export const sleeveById = (id: string): SleeveDef => SLEEVES.find((s) => s.id === id) ?? SLEEVES[0]!;

/** Per-sleeve palette: [outer, inner] background stops, border, emblem accent. */
function palette(id: string): { bg0: string; bg1: string; border: string; accent: string; emblem: string } {
  switch (id) {
    case "crimson": return { bg0: "#3a121a", bg1: "#160508", border: "#e9b65a", accent: "#ff6b7a", emblem: "#ffd98a" };
    case "abyss": return { bg0: "#141a36", bg1: "#06070f", border: "#7d6bff", accent: "#9a8bff", emblem: "#c4b8ff" };
    case "verdant": return { bg0: "#0f3325", bg1: "#04130d", border: "#3fd494", accent: "#54e6a0", emblem: "#b7ffdd" };
    case "aureate": return { bg0: "#352a0c", bg1: "#120d03", border: "#ffce5a", accent: "#ffe08a", emblem: "#fff0c0" };
    case "obsidian": return { bg0: "#16171c", bg1: "#050507", border: "#8b97ad", accent: "#c9d4e6", emblem: "#eef3fb" };
    case "prism": return { bg0: "#14224a", bg1: "#070b1c", border: "#7af0ff", accent: "#b388ff", emblem: "#eafcff" };
    case "df-cyber":
    default: return { bg0: "#16294d", bg1: "#070d1c", border: "#4fd4ff", accent: "#50d2ff", emblem: "#bfeaff" };
  }
}

/** Paint a sleeve onto a 2D context spanning (w × h). */
export function drawSleeve(g: CanvasRenderingContext2D, id: string, w: number, h: number): void {
  if (id === "default") { drawClassicBack(g, w, h); return; }
  const p = palette(id);
  g.clearRect(0, 0, w, h);

  // base gradient
  const bg = g.createLinearGradient(0, 0, w, h);
  bg.addColorStop(0, p.bg0); bg.addColorStop(1, p.bg1);
  g.fillStyle = bg; g.fillRect(0, 0, w, h);

  // corner radial sheen (subtle)
  const sheen = g.createRadialGradient(w * 0.32, h * 0.24, 0, w * 0.32, h * 0.24, w * 0.9);
  sheen.addColorStop(0, hexA(p.accent, 0.14)); sheen.addColorStop(0.5, hexA(p.accent, 0.04)); sheen.addColorStop(1, "rgba(0,0,0,0)");
  g.fillStyle = sheen; g.fillRect(0, 0, w, h);

  // texture motif per family
  g.save();
  if (id === "abyss" || id === "prism") circuit(g, w, h, hexA(p.accent, id === "prism" ? 0.16 : 0.12));
  else if (id === "verdant") circuit(g, w, h, hexA(p.accent, 0.12));
  else guilloche(g, w, h, hexA(p.accent, 0.10));
  g.restore();

  // double border frame
  const m = Math.round(w * 0.05);
  frame(g, m, m, w - 2 * m, h - 2 * m, p.border, Math.max(2, w * 0.018));
  frame(g, m * 1.7, m * 1.7, w - 3.4 * m, h - 3.4 * m, hexA(p.border, 0.4), Math.max(1, w * 0.008));

  // central emblem: a rotated rhombus crest with an inner mark
  const cx = w / 2, cy = h / 2, R = Math.min(w, h) * 0.2;
  g.save();
  g.translate(cx, cy); g.rotate(Math.PI / 4);
  // glow
  g.shadowColor = hexA(p.accent, 0.9); g.shadowBlur = w * 0.06;
  g.fillStyle = hexA(p.accent, 0.16);
  g.strokeStyle = p.border; g.lineWidth = Math.max(2, w * 0.016);
  roundRectPath(g, -R, -R, R * 2, R * 2, R * 0.16);
  g.fill(); g.stroke();
  g.shadowBlur = 0;
  // inner mark
  g.rotate(-Math.PI / 4);
  g.fillStyle = hexA(p.emblem, 0.92);
  emblemMark(g, id, R * 0.92);
  g.restore();

  // legendary: faint outer aura ring
  if (id === "prism" || id === "aureate") {
    g.strokeStyle = hexA(p.accent, 0.22); g.lineWidth = Math.max(1, w * 0.006);
    g.beginPath(); g.arc(cx, cy, R * 1.7, 0, Math.PI * 2); g.stroke();
  }
}

/** The default "sleeveless" card back, a clean, classic, TEXT-FREE design (no third-party art):
 *  a deep midnight-blue field, a tooled double border, concentric guilloché rings and a faceted
 *  central diamond crest. Premium but neutral. */
function drawClassicBack(g: CanvasRenderingContext2D, w: number, h: number): void {
  g.clearRect(0, 0, w, h);
  const cx = w / 2, cy = h / 2;
  const silver = "#aab6d6", silverDim = "rgba(150,170,210,";

  // deep base + a soft radial lift toward the centre
  const bg = g.createLinearGradient(0, 0, w, h);
  bg.addColorStop(0, "#1a2344"); bg.addColorStop(0.5, "#141b30"); bg.addColorStop(1, "#0c1020");
  g.fillStyle = bg; g.fillRect(0, 0, w, h);
  const lift = g.createRadialGradient(cx, cy, 0, cx, cy, w * 0.8);
  lift.addColorStop(0, "rgba(60,84,140,0.35)"); lift.addColorStop(0.6, "rgba(30,44,80,0.18)"); lift.addColorStop(1, "rgba(0,0,0,0)");
  g.fillStyle = lift; g.fillRect(0, 0, w, h);

  // tooled double border
  const m = Math.round(w * 0.055);
  roundRectPath(g, m, m, w - 2 * m, h - 2 * m, w * 0.05);
  g.strokeStyle = silverDim + "0.5)"; g.lineWidth = Math.max(2, w * 0.02); g.stroke();
  roundRectPath(g, m * 1.8, m * 1.8, w - 3.6 * m, h - 3.6 * m, w * 0.04);
  g.strokeStyle = silverDim + "0.28)"; g.lineWidth = Math.max(1, w * 0.008); g.stroke();

  // concentric guilloché rings
  g.strokeStyle = silverDim + "0.10)"; g.lineWidth = 1.2;
  for (let k = 0; k < 6; k++) {
    g.beginPath();
    const rr = Math.min(w, h) * (0.12 + k * 0.052);
    for (let a = 0; a <= Math.PI * 2 + 0.1; a += 0.05) {
      const r = rr + Math.sin(a * 7 + k * 0.7) * Math.min(w, h) * 0.012;
      const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r;
      a === 0 ? g.moveTo(x, y) : g.lineTo(x, y);
    }
    g.stroke();
  }

  // central faceted diamond crest
  const R = Math.min(w, h) * 0.2;
  g.save();
  g.translate(cx, cy); g.rotate(Math.PI / 4);
  g.shadowColor = "rgba(120,150,210,0.6)"; g.shadowBlur = w * 0.05;
  const crest = g.createLinearGradient(-R, -R, R, R);
  crest.addColorStop(0, "rgba(70,96,150,0.55)"); crest.addColorStop(1, "rgba(28,40,72,0.55)");
  g.fillStyle = crest; g.strokeStyle = silver; g.lineWidth = Math.max(2, w * 0.014);
  roundRectPath(g, -R, -R, R * 2, R * 2, R * 0.12); g.fill(); g.stroke();
  g.shadowBlur = 0;
  // inner facet cross
  g.strokeStyle = silverDim + "0.5)"; g.lineWidth = Math.max(1, w * 0.006);
  g.beginPath(); g.moveTo(-R, 0); g.lineTo(R, 0); g.moveTo(0, -R); g.lineTo(0, R); g.stroke();
  g.rotate(-Math.PI / 4);
  // small inner star
  g.fillStyle = "rgba(200,214,240,0.92)";
  g.beginPath();
  for (let i = 0; i < 4; i++) { const a = (i / 4) * Math.PI * 2, a2 = a + Math.PI / 4; g.lineTo(Math.cos(a) * R * 0.42, Math.sin(a) * R * 0.42); g.lineTo(Math.cos(a2) * R * 0.16, Math.sin(a2) * R * 0.16); }
  g.closePath(); g.fill();
  g.restore();

  // corner ticks
  g.strokeStyle = silverDim + "0.4)"; g.lineWidth = Math.max(1, w * 0.008);
  const t = w * 0.05, off = m * 2.4;
  const corner = (ox: number, oy: number, dx: number, dy: number) => { g.beginPath(); g.moveTo(ox, oy + dy * t); g.lineTo(ox, oy); g.lineTo(ox + dx * t, oy); g.stroke(); };
  corner(off, off, 1, 1); corner(w - off, off, -1, 1); corner(off, h - off, 1, -1); corner(w - off, h - off, -1, -1);
}

// ── motif helpers ─────────────────────────────────────────────────────────────────
function guilloche(g: CanvasRenderingContext2D, w: number, h: number, color: string): void {
  g.strokeStyle = color; g.lineWidth = 1;
  const cx = w / 2, cy = h / 2;
  for (let k = 0; k < 5; k++) {
    g.beginPath();
    const rr = (Math.min(w, h) * 0.46) * (0.4 + k * 0.14);
    for (let a = 0; a <= Math.PI * 2 + 0.1; a += 0.06) {
      const r = rr + Math.sin(a * 6 + k) * (Math.min(w, h) * 0.03);
      const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r * 1.45;
      a === 0 ? g.moveTo(x, y) : g.lineTo(x, y);
    }
    g.stroke();
  }
}
function circuit(g: CanvasRenderingContext2D, w: number, h: number, color: string): void {
  g.strokeStyle = color; g.fillStyle = color; g.lineWidth = Math.max(1, w * 0.006);
  const grid = w / 7;
  for (let i = 1; i < 7; i++) {
    const x = i * grid;
    g.beginPath(); g.moveTo(x, h * 0.1); g.lineTo(x, h * 0.4 + (i % 2) * h * 0.1); g.lineTo(x + grid * 0.5, h * 0.55); g.stroke();
    g.beginPath(); g.arc(x, h * 0.1, w * 0.012, 0, Math.PI * 2); g.fill();
  }
  for (let j = 1; j < 6; j++) {
    const y = j * (h / 6);
    g.beginPath(); g.moveTo(w * 0.1, y); g.lineTo(w * 0.45 + (j % 2) * w * 0.1, y); g.stroke();
  }
}
function frame(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, color: string, lw: number): void {
  roundRectPath(g, x, y, w, h, Math.min(w, h) * 0.06);
  g.strokeStyle = color; g.lineWidth = lw; g.stroke();
}
function emblemMark(g: CanvasRenderingContext2D, id: string, R: number): void {
  // a simple, family-flavored glyph centered at origin
  g.beginPath();
  if (id === "verdant") { // leaf/chevrons
    for (let i = -1; i <= 1; i++) { g.moveTo(-R * 0.5, i * R * 0.32 + R * 0.2); g.lineTo(0, i * R * 0.32 - R * 0.1); g.lineTo(R * 0.5, i * R * 0.32 + R * 0.2); }
    g.lineWidth = R * 0.12; g.strokeStyle = g.fillStyle as string; g.stroke(); return;
  }
  if (id === "obsidian") { g.arc(0, 0, R * 0.5, 0, Math.PI * 2); g.fill(); g.globalCompositeOperation = "destination-out"; g.beginPath(); g.arc(0, 0, R * 0.3, 0, Math.PI * 2); g.fill(); g.globalCompositeOperation = "source-over"; return; }
  // default: a four-point star
  for (let i = 0; i < 4; i++) { const a = (i / 4) * Math.PI * 2; const a2 = a + Math.PI / 4; g.lineTo(Math.cos(a) * R * 0.6, Math.sin(a) * R * 0.6); g.lineTo(Math.cos(a2) * R * 0.22, Math.sin(a2) * R * 0.22); }
  g.closePath(); g.fill();
}
function roundRectPath(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}
function hexA(hex: string, a: number): string {
  const n = hex.replace("#", "");
  const r = parseInt(n.slice(0, 2), 16), gg = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r},${gg},${b},${a})`;
}
